import React, { useEffect, useMemo } from 'react'

import { BottomBarType } from '@makyoapp/makyo-common'

import { useFrameContext } from '@/lib/react-frame'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useAppPagesContext } from '@/context/AppPagesContext'
import { useBuilderColorSchemeV2 } from '@/hooks/__project__/useBuilderColorSchemeV2'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { getComponentDirection } from '@/shared/builder-utils'
import { EVENT_KEYS } from '@/shared/custom-event'

/**
 * AppWrapper
 */
export const AppWrapper = React.memo(({ children }: { children: React.ReactNode }) => {
  const { language } = useDbClientContext()
  const { activePage } = useAppPagesContext()
  const { topBarVisibility, navbarSettings } = useAppBuilderContext()
  const [themeState] = useBuilderColorSchemeV2()
  const { document: frameDoc = document } = useFrameContext()

  const dir = getComponentDirection(language.slice(0, 2))
  const isLtr = dir === 'ltr'

  const isDarkMode = useMemo(() => {
    return themeState?.current === 'dark'
  }, [themeState])

  const {
    isNavPositionLeading,
    isNavigationBarTop,
    isNavigationBarBottom,
    activeBreakpoint,
    isSticky = false,
    getMenuWidth,
    getMenuHeight,
  } = useBreakpoint()

  const menuHeight = activeBreakpoint?.menuHeight
  const menuWidth = activeBreakpoint?.menuWidth

  const getMargin = (condition: boolean, value: string | undefined, defaultValue: string) =>
    condition ? value || defaultValue : '0px'

  const showNavbar = activePage?.bbType !== BottomBarType.Disabled && navbarSettings.desktop.show

  const modifiedMargin = showNavbar
    ? {
        marginTop: getMargin(isNavigationBarTop && !!isSticky, getMenuHeight(menuHeight), '70px'),
        marginLeft: isLtr ? getMargin(isNavPositionLeading, getMenuWidth(menuWidth), '100px') : undefined,
        marginRight: !isLtr ? getMargin(isNavPositionLeading, getMenuWidth(menuWidth), '100px') : undefined,
        marginBottom: getMargin(isNavigationBarBottom, topBarVisibility ? '0px' : '0px', '70px'),
      }
    : {}

  /**
   * scroll to top when builder scroll top event is triggered
   */
  useEffect(() => {
    if (!frameDoc) return

    const handleScrollToTop = () => {
      frameDoc?.documentElement.scrollTo(0, 0)
    }
    window.addEventListener(EVENT_KEYS.BUILDER_SCROLL_TOP, handleScrollToTop)
    return () => window.removeEventListener(EVENT_KEYS.BUILDER_SCROLL_TOP, handleScrollToTop)

    //
  }, [frameDoc])

  return (
    <div
      id='app-wrapper'
      dir={dir}
      data-theme-mode={isDarkMode ? 'dark' : 'light'}
      style={{
        ...modifiedMargin,
        height: '100%',
      }}
    >
      {children}
    </div>
  )
})
AppWrapper.displayName = 'AppWrapper'
